import { forwardRef, useEffect, type KeyboardEvent } from 'react'
import { cn } from '@wellesley/ui'
import { handleMarkdownEditorKeyDown } from './handlers'
import RichTextArea from '../../RichTextArea'
import { MarkdownActions } from '../MarkdownEditorState/actions'

export type PlainMarkdownEditorProps = {
  id?: string
  name: string
  disabled?: boolean
  placeholder?: string
  className?: string
  value: string
  onChange: (val: string) => void
  onBlur?: () => void
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void
  actions: MarkdownActions
  focusTrigger?: number
}

export const PlainMarkdownEditor = forwardRef<
  HTMLTextAreaElement,
  PlainMarkdownEditorProps
>(
  (
    {
      id,
      name,
      disabled,
      placeholder,
      className,
      value,
      onChange,
      onBlur,
      onKeyDown,
      actions,
      focusTrigger,
    },
    ref,
  ) => {
    useEffect(() => {
      if (focusTrigger !== undefined && ref && 'current' in ref) {
        const textarea = ref.current
        if (textarea) {
          textarea.focus()
          const length = textarea.value.length
          textarea.setSelectionRange(length, length)
        }
      }
    }, [focusTrigger, ref])

    return (
      <RichTextArea
        id={id}
        ref={ref}
        name={name}
        data-testid={name}
        placeholder={placeholder}
        className={cn('rounded-m w-full !outline-none focus:ring-0', className)}
        text={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        setText={(newValue) => {
          onChange(newValue)
        }}
        onKeyDown={(e) => {
          // Allow parent handlers to process first (e.g., Mod shortcuts)
          onKeyDown?.(e)
          if (e?.defaultPrevented) return

          handleMarkdownEditorKeyDown(e, actions)
        }}
        onBlur={onBlur}
        rowsCount={14}
        disabled={disabled}
        isForm
      />
    )
  },
)

PlainMarkdownEditor.displayName = 'PlainMarkdownEditor'
